import { useEffect, useState } from "react";
import api, { fmtDateTime } from "../lib/api";
import { useAuth } from "../lib/auth";
import Topbar from "../components/Topbar";
import EmptyState from "../components/EmptyState";
import KpiCard from "../components/KpiCard";
import StatusBadge from "../components/StatusBadge";
import { Bell, CheckCircle2, AlertTriangle, AlertCircle, Info, RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const OPS_ROLES = ["corporate_admin", "treasury", "branch_manager", "operativo", "hr_admin", "colaborador", "mantenimiento", "direccion"];

const SEV_ICON = {
  critical: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

const SEV_BG = {
  critical: "bg-red-50 border-red-200",
  warning: "bg-amber-50 border-amber-200",
  info: "bg-blue-50 border-blue-200",
};

const SEV_ICON_BG = {
  critical: "bg-red-100 text-red-700",
  warning: "bg-amber-100 text-amber-700",
  info: "bg-blue-100 text-blue-700",
};

export default function Alerts() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [list, setList] = useState([]);
  // Fix WH11 (2026-08-30): la pestaña inicial era "unread", así que una alerta
  // recién abierta (ya leída) DESAPARECÍA al volver a la pantalla y no había
  // forma de reubicarla (video de Claudia 25/08, min 2:47). Ahora se entra por
  // "Todas": las no leídas resaltan y las leídas quedan visibles al 60%.
  const [tab, setTab] = useState("all");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    // Fix WH20 (2026-08-31): A1 del video de Claudia (min 0:24) — "las
    // notificaciones no llevan a ningun lado". Medido en produccion el 31/08
    // sobre las 80 alertas de compras del corporativo: **0 tenian action_url**,
    // que es el unico campo que este componente miraba para navegar. 32 tenian
    // `deep_link` (el nombre que usa el backend) y el front lo ignoraba; las
    // otras 48 no tenian ninguno de los dos. Resultado: clic en una alerta de
    // compras = no pasaba absolutamente nada.
    // Se arregla en dos pasos: aca se acepta `deep_link` y, si no hay ninguno,
    // se deriva el destino de entity_type/entity_id — eso alcanza para que las
    // alertas VIEJAS que ya estan en la base tambien se vuelvan clickeables,
    // sin migrar ni un documento.
    const comprasFallback = (a) => {
      const id = a.entity_id;
      if (a.entity_type === "purchase_order" && id) return `/ocs/${id}`;
      if (a.entity_type === "supplier" && id) return `/proveedores/${id}`;
      if (a.entity_type === "invoice") return "/facturas";
      if (a.entity_type === "goods_receipt") return "/recepciones";
      return null;
    };
    const compras = await api.get("/alerts").then(r => (r.data || []).map(a => ({
      ...a,
      source: "compras",
      action_url: a.action_url || a.deep_link || comprasFallback(a),
    }))).catch(() => []);
    let ops = [];
    // Fix WH78 (2026-10-15): Legal recibe los avisos de vigencias (marcas).
    if (OPS_ROLES.includes(user?.role) || user?.role === "legal") {
      ops = await api.get("/ops/alerts/me").then(r => (r.data?.alerts || []).map(a => ({
        id: a.id,
        source: "ops",
        title: a.title,
        message: a.body,
        severity: a.severity,
        created_at: a.created_at,
        read: (a.read_by || []).some(rb => rb.user_id === user.id),
        action_url: a.deep_link || "/ops",
      }))).catch(() => []);
    }
    setList([...compras, ...ops].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
  };
  useEffect(() => { load(); }, [user?.role]);

  const markRead = async (a) => {
    try {
      if (a.source === "ops") await api.post(`/ops/alerts/${a.id}/read`);
      else await api.post(`/alerts/${a.id}/read`);
      load();
    } catch {}
  };

  const markAllRead = async () => {
    setBusy(true);
    try {
      const unread = list.filter((a) => !a.read);
      await Promise.all(unread.map((a) => a.source === "ops" ? api.post(`/ops/alerts/${a.id}/read`) : api.post(`/alerts/${a.id}/read`)));
      toast.success(t("alerts.markedAllRead"));
      load();
    } finally { setBusy(false); }
  };

  const regen = async () => {
    setBusy(true);
    try {
      const r = await api.post("/alerts/generate");
      toast.success(t("alerts.regenerated", { n: r.data.new || 0 }));
      load();
    } catch (e) {
      toast.error(t("common.error"));
    } finally { setBusy(false); }
  };

  const handleClick = (a) => {
    if (!a.read) markRead(a);
    if (a.action_url) navigate(a.action_url);
  };

  const unread = list.filter((a) => !a.read);
  const critical = unread.filter((a) => a.severity === "critical");
  const warning = unread.filter((a) => a.severity === "warning");
  const info = unread.filter((a) => a.severity === "info");

  let filtered = list;
  if (tab === "unread") filtered = unread;
  else if (tab === "critical") filtered = list.filter((a) => a.severity === "critical");
  else if (tab === "warning") filtered = list.filter((a) => a.severity === "warning");
  else if (tab === "info") filtered = list.filter((a) => a.severity === "info");

  return (
    <>
      <Topbar title={t("alerts.title")} subtitle={t("alerts.subtitle", { unread: unread.length, total: list.length })} />
      <div className="p-6 space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <KpiCard testId="kpi-alerts-critical" label={t("alerts.kpiCritical")} value={critical.length} icon={AlertCircle} accent={critical.length > 0} />
          <KpiCard testId="kpi-alerts-warning" label={t("alerts.kpiWarning")} value={warning.length} icon={AlertTriangle} />
          <KpiCard testId="kpi-alerts-info" label={t("alerts.kpiInfo")} value={info.length} icon={Info} />
        </div>

        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl p-1">
            {[
              { v: "unread", l: t("alerts.tabUnread") },
              { v: "critical", l: t("alerts.tabCritical") },
              { v: "warning", l: t("alerts.tabWarning") },
              { v: "info", l: t("alerts.tabInfo") },
              { v: "all", l: t("alerts.tabAll") },
            ].map((f) => (
              <button key={f.v} onClick={() => setTab(f.v)} data-testid={`alerts-tab-${f.v}`}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition ${tab === f.v ? "bg-[#1A1A1A] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                {f.l}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="alerts-regen-btn" onClick={regen} disabled={busy}
              className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold flex items-center gap-1.5 hover:bg-slate-50 disabled:opacity-50">
              <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> {t("alerts.regenerate")}
            </button>
            {unread.length > 0 && (
              <button data-testid="alerts-markall-btn" onClick={markAllRead} disabled={busy}
                className="px-3 py-2 bg-[#1A1A1A] text-white rounded-xl text-xs font-bold flex items-center gap-1.5 disabled:opacity-50">
                <CheckCircle2 className="w-3.5 h-3.5" /> {t("alerts.markAllRead")}
              </button>
            )}
          </div>
        </div>

        {filtered.length === 0 ? (
          <EmptyState title={t("alerts.emptyTitle")} message={t("alerts.emptyMsg")} icon={CheckCircle2} />
        ) : (
          <div className="space-y-2.5">
            {filtered.map((a, idx) => {
              const Icon = SEV_ICON[a.severity] || Bell;
              return (
                <motion.div key={a.id} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.03 }}
                  onClick={() => handleClick(a)}
                  className={`bg-white border ${SEV_BG[a.severity] || "border-slate-200"} rounded-2xl p-4 flex items-start gap-3 cursor-pointer hover:shadow-sm ${a.read ? "opacity-60" : ""}`}
                  data-testid={`alert-row-${a.id}`}>
                  <div className={`w-10 h-10 rounded-xl ${SEV_ICON_BG[a.severity] || "bg-slate-100 text-slate-600"} flex items-center justify-center flex-shrink-0`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <StatusBadge status={a.severity} />
                      {a.source === "ops" && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500" data-testid={`alert-source-${a.id}`}>OPS</span>
                      )}
                      <span className="text-xs text-slate-500">{fmtDateTime(a.created_at)}</span>
                    </div>
                    <div className="mt-1 text-sm font-semibold text-[#1A1A1A]">{a.title}</div>
                    <div className="text-xs text-slate-600 mt-0.5">{a.message}</div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
